import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Plug, BarChart3 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { TenantTierManager } from './TenantTierManager';
import { useSearchParams } from 'react-router-dom';
import IntegracaoMonitorPage from '@/pages/IntegracaoMonitorPage';
import { toast } from 'sonner';

export const EmpConfiguracoesView: React.FC = () => {
  const { activeClusterIds } = useEmpFeatureFlag();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'geral';
  const queryClient = useQueryClient();

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['emp-config-all', activeClusterIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_config')
        .select('*')
        .in('cluster_id', activeClusterIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: activeClusterIds.length > 0,
  });

  // UX Analytics flag state
  const uxAnalyticsEnabled = configs.some((c: any) => {
    const cfg = c.configuracoes || {};
    return cfg.ux_analytics_enabled === true;
  });

  const toggleUxAnalytics = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (configs.length === 0) {
        // Create a new emp_config for the first active cluster
        if (activeClusterIds.length === 0) {
          throw new Error('Nenhum cluster ativo encontrado.');
        }
        const { error } = await supabase
          .from('emp_config')
          .insert({
            cluster_id: activeClusterIds[0],
            modulo_ativo: true,
            configuracoes: { ux_analytics_enabled: enabled },
          } as any);
        if (error) throw error;
      } else {
        const target = configs[0] as any;
        const currentConfig = target.configuracoes || {};
        const newConfig = { ...currentConfig, ux_analytics_enabled: enabled };

        const { error } = await supabase
          .from('emp_config')
          .update({ configuracoes: newConfig } as any)
          .eq('id', target.id);
        if (error) throw error;
      }
    },
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['emp-config-all'] });
      queryClient.invalidateQueries({ queryKey: ['ux-analytics-flag'] });
      toast.success(enabled ? 'Coleta de métricas UX ativada' : 'Coleta de métricas UX desativada');
    },
    onError: (err: any) => {
      toast.error('Erro ao atualizar flag: ' + err.message);
    },
  });

  if (isLoading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="self-start">
          <TabsTrigger value="geral" className="gap-2 text-xs sm:text-sm">
            <Settings className="h-4 w-4" />
            Geral
          </TabsTrigger>
          <TabsTrigger value="integracao-erp" className="gap-2 text-xs sm:text-sm">
            <Plug className="h-4 w-4" />
            Integração ERP
          </TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="mt-4">
          <div className="space-y-4">
            <TenantTierManager />

            {/* UX Analytics Toggle — admin only */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Coleta de Métricas UX
                </CardTitle>
                <CardDescription className="text-xs">
                  Quando ativada, eventos de uso (Quick Capture, acesso a abas) são registrados para análise de adoção. Visível apenas em Observabilidade → Adoção UX.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Switch
                    id="ux-analytics-toggle"
                    checked={uxAnalyticsEnabled}
                    onCheckedChange={(checked) => toggleUxAnalytics.mutate(checked)}
                    disabled={toggleUxAnalytics.isPending || activeClusterIds.length === 0}
                  />
                  <Label htmlFor="ux-analytics-toggle" className="text-sm">
                    {uxAnalyticsEnabled ? 'Ativada' : 'Desativada'}
                  </Label>
                  {activeClusterIds.length === 0 && (
                    <span className="text-xs text-muted-foreground">(Necessário ao menos um cluster ativo)</span>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Configurações</p><p className="text-2xl font-bold">{configs.length}</p></CardContent></Card>
              <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Módulos Ativos</p><p className="text-2xl font-bold text-green-600">{configs.filter((c: any) => c.modulo_ativo).length}</p></CardContent></Card>
              <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Clusters</p><p className="text-2xl font-bold">{activeClusterIds.length}</p></CardContent></Card>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4" /> Parâmetros do Módulo</CardTitle></CardHeader>
              <CardContent className="p-0">
                {configs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">Nenhuma configuração encontrada.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cluster</TableHead>
                        <TableHead>Módulo Ativo</TableHead>
                        <TableHead>Configurações</TableHead>
                        <TableHead>Atualizado em</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {configs.map((c: any) => {
                        const configObj = c.configuracoes || {};
                        const configKeys = Object.keys(configObj);
                        return (
                          <TableRow key={c.id}>
                            <TableCell className="font-medium text-sm">{c.cluster_id?.slice(0, 8)}…</TableCell>
                            <TableCell>
                              <Badge variant={c.modulo_ativo ? 'default' : 'outline'}>
                                {c.modulo_ativo ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {configKeys.slice(0, 5).map(key => (
                                  <div key={key} className="text-xs">
                                    <span className="font-mono text-muted-foreground">{key}:</span>{' '}
                                    <code className="bg-muted px-1 rounded">
                                      {typeof configObj[key] === 'object' ? JSON.stringify(configObj[key]).slice(0, 50) + '…' : String(configObj[key])}
                                    </code>
                                  </div>
                                ))}
                                {configKeys.length > 5 && <p className="text-xs text-muted-foreground">+{configKeys.length - 5} mais...</p>}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{c.updated_at?.slice(0, 10) || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="integracao-erp" className="mt-4">
          <IntegracaoMonitorPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
};
